import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Check, X, Ban, Trash2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Common";
import { DatePicker } from "@/components/DatePicker";
import { LeaveCalendar } from "@/components/LeaveCalendar";
import { LeaveBalanceCard } from "@/components/LeaveBalanceCard";
import { MyPayslipsCard } from "@/components/MyPayslipsCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPES = { annual: "Annual", sick: "Sick", maternity_paternity: "Maternity / Paternity", unpaid: "Unpaid", compassionate: "Compassionate", other: "Other" };
const STATUS = {
  pending: "bg-amber-50 text-amber-800 ring-amber-200", approved: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  rejected: "bg-red-50 text-red-700 ring-red-200", cancelled: "bg-slate-100 text-slate-600 ring-slate-200",
};
const today = () => new Date().toISOString().slice(0, 10);
const onErr = (e) => toast.error(e?.response?.data?.detail || "Request failed");

/** Leave requests — staff submit their own; admins see all and approve / reject. */
export default function Leave() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState("all");
  const [mine, setMine] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [deciding, setDeciding] = useState(null); // {req, decision}
  const params = { ...(status !== "all" ? { status } : {}), ...(mine ? { mine: "1" } : {}) };
  const { data, isLoading } = useQuery({ queryKey: ["leave", params], queryFn: () => api.listLeave(params) });
  const rows = data?.rows || [];
  const refresh = () => { qc.invalidateQueries({ queryKey: ["leave"] }); qc.invalidateQueries({ queryKey: ["leave-pending"] }); qc.invalidateQueries({ queryKey: ["leave-calendar"] }); qc.invalidateQueries({ queryKey: ["leave-balances"] }); };
  const cancelMut = useMutation({ mutationFn: (id) => api.cancelLeave(id), onSuccess: () => { toast.success("Request cancelled"); refresh(); }, onError: onErr });
  const delMut = useMutation({ mutationFn: (id) => api.deleteLeave(id), onSuccess: () => { toast.success("Request deleted"); refresh(); }, onError: onErr });

  return (
    <div data-testid="leave-page">
      <PageHeader overline="HR" title="Leave"
        subtitle={isAdmin ? "All leave requests. Approve or reject with a note — the employee is emailed." : "Submit your leave requests and follow their approval."}
        actions={<Button data-testid="leave-new-btn" onClick={() => setNewOpen(true)}><Plus className="size-4 mr-1.5" /> Request leave</Button>} />

      <LeaveBalanceCard balance={data?.my_balance} pendingCount={data?.pending_count || 0} isAdmin={isAdmin} />
      <MyPayslipsCard />

      <LeaveCalendar />

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {["all", "pending", "approved", "rejected", "cancelled"].map((v) => (
          <button key={v} type="button" data-testid={`leave-filter-${v}`} onClick={() => setStatus(v)}
                  className={`px-3 py-1.5 rounded-full text-xs border capitalize ${status === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-border"}`}>{v}</button>
        ))}
        {isAdmin && <label className="ml-auto flex items-center gap-2 text-xs"><Checkbox data-testid="leave-mine-toggle" checked={mine} onCheckedChange={(v) => setMine(!!v)} /> Only my requests</label>}
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : rows.length === 0 ? (
        <div data-testid="leave-empty"><EmptyState title="No leave requests" hint="Nothing matches this filter." /></div>
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">{["Employee", "Type", "Dates", "Days", "Reason", "Status", ""].map((h) => <TableHead key={h} className="overline">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => {
                const mineRow = r.requester_user_email === (user?.email || "").toLowerCase();
                return (
                  <TableRow key={r.id} data-testid={`leave-row-${r.id}`}>
                    <TableCell className="font-medium text-sm">{r.employee_name}<div className="text-[10px] text-muted-foreground mono">{r.requester_email}</div></TableCell>
                    <TableCell className="text-sm">{TYPES[r.type] || r.type}</TableCell>
                    <TableCell className="mono text-xs">{fmtDate(r.start_date)}{r.end_date !== r.start_date ? ` → ${fmtDate(r.end_date)}` : ""}{r.half_day ? " (½ day)" : ""}</TableCell>
                    <TableCell className="mono text-sm">{r.days}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[220px]"><div className="truncate" title={r.reason}>{r.reason || "—"}</div>{r.decision_note ? <div className="text-[10px] text-slate-700 italic">“{r.decision_note}”</div> : null}
                      {r.attachment && <button type="button" className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline mt-0.5" data-testid={`leave-attachment-${r.id}`} onClick={() => api.openLeaveAttachment(r.id).catch(onErr)}><Paperclip className="size-3" /> {r.attachment.name}</button>}</TableCell>
                    <TableCell><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 ring-inset capitalize ${STATUS[r.status]}`} data-testid={`leave-status-${r.id}`}>{r.status}</span>
                      {r.override && <div className="text-[10px] text-amber-700">override</div>}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {r.status === "pending" && isAdmin && (<>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid={`leave-approve-${r.id}`} onClick={() => setDeciding({ req: r, decision: "approve" })}><Check className="size-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-600 ml-1" data-testid={`leave-reject-${r.id}`} onClick={() => setDeciding({ req: r, decision: "reject" })}><X className="size-3.5" /></Button>
                      </>)}
                      {r.status === "pending" && (mineRow || isAdmin) && (
                        <Button size="sm" variant="ghost" className="ml-1" data-testid={`leave-cancel-${r.id}`} disabled={cancelMut.isPending} onClick={() => cancelMut.mutate(r.id)}><Ban className="size-3.5" /></Button>)}
                      {isAdmin && r.status !== "pending" && (
                        <Button size="sm" variant="ghost" className="text-red-600" data-testid={`leave-delete-${r.id}`} onClick={() => delMut.mutate(r.id)}><Trash2 className="size-3.5" /></Button>)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <NewLeaveDialog open={newOpen} onClose={() => setNewOpen(false)} onDone={refresh} isAdmin={isAdmin} />
      <DecideDialog item={deciding} onClose={() => setDeciding(null)} onDone={refresh} />
    </div>
  );
}

function NewLeaveDialog({ open, onClose, onDone, isAdmin }) {
  const empty = { type: "annual", start_date: today(), end_date: today(), half_day: false, reason: "", employee_id: "", employee_name: "" };
  const [f, setF] = useState(empty);
  const [file, setFile] = useState(null);
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: api.listEmployees, enabled: open && isAdmin });
  const mut = useMutation({
    mutationFn: async () => {
      const r = await api.createLeave({ ...f, employee_id: f.employee_id || undefined, employee_name: f.employee_name || undefined });
      if (file) {
        try { await api.uploadLeaveAttachment(r.id, file); }
        catch (e) { toast.error(`Request saved but the attachment failed: ${e?.response?.data?.detail || "upload error"}`); }
      }
      return r;
    },
    onSuccess: (r) => { toast.success(`Leave requested — ${r.days} working day${r.days === 1 ? "" : "s"}`); setF(empty); setFile(null); onClose(); onDone(); },
    onError: onErr,
  });
  const needsCert = f.type === "sick" || f.type === "compassionate";
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="leave-dialog">
        <DialogHeader><DialogTitle>Request leave</DialogTitle><DialogDescription className="text-xs">Days are counted Monday–Friday, skipping public holidays. Admins are notified by email.</DialogDescription></DialogHeader>
        <form className="grid grid-cols-1 sm:grid-cols-2 gap-3" onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}>
          {isAdmin && (
            <div className="sm:col-span-2"><Label className="overline">On behalf of (optional)</Label>
              <Select value={f.employee_id || "me"} onValueChange={(v) => setF((s) => ({ ...s, employee_id: v === "me" ? "" : v }))}>
                <SelectTrigger data-testid="leave-employee"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="me">Myself</SelectItem>{employees.filter((e) => e.active).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <div><Label className="overline">Type</Label>
            <Select value={f.type} onValueChange={set("type")}>
              <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select></div>
          <label className="flex items-center gap-2 text-sm self-end pb-2"><Checkbox data-testid="leave-half-day" checked={f.half_day} onCheckedChange={(v) => setF((s) => ({ ...s, half_day: !!v, end_date: v ? s.start_date : s.end_date }))} /> Half day</label>
          <div><Label className="overline">From</Label><DatePicker testId="leave-start" value={f.start_date} onChange={(v) => setF((s) => ({ ...s, start_date: v, end_date: s.half_day || s.end_date < v ? v : s.end_date }))} /></div>
          <div><Label className="overline">To</Label><DatePicker testId="leave-end" value={f.end_date} onChange={set("end_date")} disabled={f.half_day ? true : { before: new Date(f.start_date) }} /></div>
          <div className="sm:col-span-2"><Label className="overline">Reason</Label><Textarea data-testid="leave-reason" rows={2} value={f.reason} onChange={set("reason")} /></div>
          <div className={`sm:col-span-2 rounded-md border p-3 ${needsCert ? "border-amber-200 bg-amber-50" : "border-border"}`} data-testid="leave-attachment-box">
            <Label className="overline flex items-center gap-1"><Paperclip className="size-3" /> {needsCert ? "Medical certificate / supporting document" : "Attachment (optional)"}</Label>
            <Input data-testid="leave-attachment-input" type="file" accept="image/*,application/pdf" capture="environment" className="mt-1 text-xs"
                   onChange={(e) => { const x = e.target.files?.[0]; if (x && x.size > 5 * 1024 * 1024) { toast.error("File too large (max 5 MB)"); e.target.value = ""; return; } setFile(x || null); }} />
            <p className="text-[11px] text-muted-foreground mt-1">{needsCert ? "Recommended for sick and compassionate leave — the approver can open it from the request." : "Image or PDF, max 5 MB."}{file ? ` Selected: ${file.name}` : ""}</p>
          </div>
          <DialogFooter className="sm:col-span-2"><Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="leave-submit" disabled={mut.isPending}>Submit request</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DecideDialog({ item, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [override, setOverride] = useState(false);
  const mut = useMutation({
    mutationFn: () => api.decideLeave(item.req.id, { decision: item.decision, note, override }),
    onSuccess: (r) => { toast.success(`Request ${r.status}`); setNote(""); setOverride(false); onClose(); onDone(); },
    onError: onErr,
  });
  if (!item) return null;
  const approve = item.decision === "approve";
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="leave-decide-dialog">
        <DialogHeader><DialogTitle>{approve ? "Approve" : "Reject"} leave — {item.req.employee_name}</DialogTitle>
          <DialogDescription className="text-xs">{TYPES[item.req.type]} · {fmtDate(item.req.start_date)} → {fmtDate(item.req.end_date)} · {item.req.days} day(s)
            {item.req.balance_at_request && ` · balance at request: ${item.req.balance_at_request.remaining} of ${item.req.balance_at_request.entitlement}`}</DialogDescription></DialogHeader>
        {item.req.attachment ? (
          <Button type="button" variant="outline" size="sm" className="justify-start" data-testid="leave-decide-attachment" onClick={() => api.openLeaveAttachment(item.req.id).catch(onErr)}>
            <Paperclip className="size-3.5 mr-1.5" /> View attachment — {item.req.attachment.name}</Button>
        ) : (item.req.type === "sick" || item.req.type === "compassionate") && <p className="text-xs text-amber-700" data-testid="leave-decide-no-attachment">No medical certificate / document attached.</p>}
        <Input data-testid="leave-decide-note" placeholder="Note to employee (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
        {approve && item.req.type === "annual" && (
          <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="leave-override" checked={override} onCheckedChange={(v) => setOverride(!!v)} /> Override annual leave balance</label>)}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="leave-decide-confirm" className={approve ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"} disabled={mut.isPending} onClick={() => mut.mutate()}>{approve ? "Approve" : "Reject"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
